import { Button, type DialogRef, OdinIcon, SplitButton } from '@myosh/odin-components';
import { memo, useCallback, useMemo, useRef } from 'react';
import type { BowtieRequestWithId } from '../../api/@types/enhanced-bowtie-api.types';
import { useAppPermissionsContext } from '../../context/app-permissions.context';
import { useDiagramContext } from '../../context/diagram.context';
import { useUserPermissionsContext } from '../../context/user-permissions.context';
import { useAIAnalysis } from '../../hooks/use-ai-analysis';
import useCloneBowtie from '../../hooks/use-clone-bowtie-button-action';
import AiAnalysisConfirmationModal from './ai-analysis-confirmation.modal';
import BowtieWizard, { type BowtieWizardRef } from './bowtie-wizard';
import ImportBowtieDiagram from './import-bowtie-diagram';

interface AISplitButtonProps {
  onAIAnalysisReady: (text: string) => void;
  bowtieDtoAnalyze?: BowtieRequestWithId;
}

/**
 * A component that renders either a SplitButton or a regular Button based on AI feature availability.
 * The SplitButton provides additional AI-related functionality like AI Wizard, Import, and Analysis options.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onAIAnalysisReady - Callback function triggered when AI analysis is complete
 * @param {Object} props.bowtieDtoAnalyze - DTO containing bowtie diagram data for AI analysis
 *
 * @example
 * ```tsx
 * <AISplitButton
 *   onAIAnalysisReady={(result) => handleAnalysisResult(result)}
 *   bowtieDtoAnalyze={bowtieDiagramData}
 * />
 * ```
 *
 * @remarks
 * - Displays a SplitButton when AI features are enabled, otherwise shows a regular Button
 * - Provides options for AI Wizard, Import functionality, and AI Analysis
 * - Handles confirmation dialogs for AI analysis with option to remember user preference
 * - Integrates with the diagram context to manage state and permissions
 */
const AISplitButton = ({ onAIAnalysisReady, bowtieDtoAnalyze }: AISplitButtonProps) => {
  const wizardRef = useRef<BowtieWizardRef>(null);
  const importBowtieDiagramRef = useRef<HTMLInputElement>(null);
  const aiAnalysisConfirmationModalRef = useRef<DialogRef>(null);

  const { isNewDiagram, isAIGenerated, isSavedDiagram, recordId } = useDiagramContext();
  // FIXME: the ai analysis should fetch the data from the context/state
  const { runAIAnalysis, isLoading: aiAnalysisRunning } = useAIAnalysis(onAIAnalysisReady, bowtieDtoAnalyze!);

  const { handleClick: handleCloneClick } = useCloneBowtie();

  // FIXME:  'aiEnabled' should be moved out of the user permissions context
  const { aiEnabled } = useUserPermissionsContext();

  const { canCreateBackendRecordsPermission } = useAppPermissionsContext();

  const hideAiAnalysisConfirmation = localStorage.getItem('hideAiAnalysisConfirmation');

  const handleBowtieWizardClick = useCallback(() => {
    return wizardRef.current?.open();
  }, []);

  const handleImportBowtie = useCallback(() => {
    importBowtieDiagramRef.current?.click();
  }, []);

  const handleRunAiAnalysis = useCallback(() => {
    if (!hideAiAnalysisConfirmation) {
      aiAnalysisConfirmationModalRef.current?.show();
      return;
    }

    runAIAnalysis();
  }, [hideAiAnalysisConfirmation, runAIAnalysis]);

  const handleSubmitConfirmation = useCallback(
    (isAiAnalysisConfirmationCheckboxChecked: boolean) => {
      if (isAiAnalysisConfirmationCheckboxChecked) {
        localStorage.setItem('hideAiAnalysisConfirmation', 'true');
      }

      runAIAnalysis();
      aiAnalysisConfirmationModalRef.current?.close();
    },
    [runAIAnalysis]
  );

  const handleAddBowtie = useCallback(() => {
    window.open(`${window.location.origin}${window.location.pathname}`);
  }, []);

  // Determine if we should show the Clone button:
  // - Must have actual records (not a draft and not AI-generated)
  // - Must have recordId
  // - User must have permission to create backend records
  const canShowClone = !isAIGenerated && !isSavedDiagram && !!recordId && canCreateBackendRecordsPermission;

  const splitButtonItems = useMemo(
    () => [
      {
        label: 'Bowtie AI Wizard',
        command: handleBowtieWizardClick,
        icon: <OdinIcon icon="Sparkles" className="size-4 mr-2" />,
      },
      {
        label: 'Import Bowtie',
        command: handleImportBowtie,
        icon: <OdinIcon icon="Upload" className="size-4 mr-2" />,
      },
      {
        label: 'Clone Bowtie',
        command: handleCloneClick,
        visible: canShowClone,
        icon: <OdinIcon icon="Copy" className="size-4 mr-2" />,
      },
      {
        label: 'AI Analysis',
        command: handleRunAiAnalysis,
        visible: !isNewDiagram,
        disabled: aiAnalysisRunning,
        icon: <OdinIcon icon="Terminal" className="size-4 mr-2" />,
      },
    ],
    [
      handleBowtieWizardClick,
      handleImportBowtie,
      handleCloneClick,
      handleRunAiAnalysis,
      isNewDiagram,
      aiAnalysisRunning,
      canShowClone,
    ]
  );

  return (
    <>
      {aiEnabled ? (
        <SplitButton
          data-cy="add-bowtie-split-button"
          label="Add Bowtie"
          onClick={handleAddBowtie}
          model={splitButtonItems}
          variant="alternative"
          className="min-w-52"
          icon={<OdinIcon icon="Plus" className="size-4 mr-2" />}
        />
      ) : (
        <Button
          data-cy="add-bowtie-button"
          classNames="min-w-52 items-center"
          variant="alternative"
          type="primary"
          onClick={handleAddBowtie}
        >
          <OdinIcon icon="Plus" className="size-4 mr-2" />
          Add Bowtie
        </Button>
      )}
      <BowtieWizard ref={wizardRef} />
      <ImportBowtieDiagram ref={importBowtieDiagramRef} />
      <AiAnalysisConfirmationModal
        ref={aiAnalysisConfirmationModalRef}
        handleSubmitConfirmation={handleSubmitConfirmation}
        handleCloseModal={() => aiAnalysisConfirmationModalRef.current?.close()}
      />
    </>
  );
};

export default memo(AISplitButton);
